/**
 * Parseur des horaires Google Maps — étape 1 de la prospection.
 *
 * Le run de calibration (50 fiches `plombier` / Oise, 29/10/2026) a montré 4 formes brutes :
 *   "08:30 to 17:00"                      → 1 plage
 *   "08:00 to 12:00, 13:00 to 18:00"      → 2 plages (pause déjeuner)
 *   "Fermé"                               → jour fermé
 *   "Ouvert 24h/24"                       → continuité
 *
 * Deux pièges mesurés, tous deux couverts par les tests :
 *   1. même avec `language: "fr"`, le séparateur reste **" to " en anglais** alors que les jours
 *      (`jeudi`) et `Fermé` sont en français — un parseur qui cherche "-" trouve 0 plage ;
 *   2. les 38 fiches à horaires commencent par le **jour du scrape** (`jeudi`), pas par lundi :
 *      sans réordonnancement, l'affichage hebdomadaire est faux.
 *
 * Aucune dépendance, aucune I/O : tout est pur et testable sans émulateur.
 */

/** Clés normalisées, dans l'ordre d'affichage (lundi → dimanche). */
export const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

/** Le JSON source est en français et sans clé normalisée : on traduit vers l'anglais. */
export const DAY_MAP = {
  lundi: 'monday',
  mardi: 'tuesday',
  mercredi: 'wednesday',
  jeudi: 'thursday',
  vendredi: 'friday',
  samedi: 'saturday',
  dimanche: 'sunday'
};

/** Libellés français pour l'UI (badge « ouvre lundi 08:30 »). */
export const DAYS_LABELS = {
  monday: 'lundi',
  tuesday: 'mardi',
  wednesday: 'mercredi',
  thursday: 'jeudi',
  friday: 'vendredi',
  saturday: 'samedi',
  sunday: 'dimanche'
};

const HEURE_REGEX = /^(\d{1,2}):(\d{2})$/;

/** `9:00` → `09:00`, `8:5` → `null` (heure invalide, jamais devinée). */
const zeroterHeure = (valeur) => {
  const match = HEURE_REGEX.exec(String(valeur ?? '').trim());
  if (!match) return null;
  const heures = Number(match[1]);
  if (heures > 23) return null;
  const minutes = match[2];
  if (Number(minutes) > 59) return null;
  return `${String(heures).padStart(2, '0')}:${minutes}`;
};

/**
 * Parse la valeur d'une journée : `["08:30-17:00"]`, `[]` si la forme est inconnue.
 * @param {string|null|undefined} hours
 * @returns {string[]|null} `null` si le jour est fermé
 */
export const parsePlages = (hours) => {
  if (hours === null || hours === undefined) return null;

  const texte = String(hours).trim();
  if (!texte) return null;

  // "Fermé", "Ferme", ... → jour fermé
  if (/^ferm[ée]/i.test(texte)) return null;

  const plages = texte
    .split(',')
    .map((segment) =>
      segment
        .trim()
        // Piège n°1 : le séparateur est " to " (anglais) même en français
        .replace(/\s+to\s+/i, '-')
        .replace(/\s*-\s*/g, '-')
    )
    .map((segment) => {
      const [debut, fin] = segment.split('-');
      const d = zeroterHeure(debut);
      const f = zeroterHeure(fin);
      return d && f ? `${d}-${f}` : null;
    })
    .filter(Boolean);

  return plages.length > 0 ? plages : [];
};

/** Vrai si la valeur annonce une ouverture continue (« Ouvert 24h/24 », « 24 heures »). */
export const estAlwaysOpen = (hours) =>
  hours !== null && hours !== undefined && /24\s*h/i.test(String(hours));

/**
 * Normalise `openingHours[]` en objet à 7 clés fixes, réordonné lundi → dimanche.
 * Une entrée dont le jour est inconnu est ignorée ; un jour absent vaut `null` (jamais `undefined`).
 *
 * @param {Array<{day?: string, hours?: string}>|null|undefined} openingHours
 * @returns {{monday: string[]|null, tuesday: string[]|null, wednesday: string[]|null,
 *            thursday: string[]|null, friday: string[]|null, saturday: string[]|null,
 *            sunday: string[]|null, alwaysOpen: boolean}|null}
 *          `null` si la source est absente — c'est le cas de 12 fiches sur 50, et il signifie
 *          « inconnu », pas « fermé ».
 */
export const parseOpeningHours = (openingHours) => {
  if (!Array.isArray(openingHours) || openingHours.length === 0) return null;

  const jours = {};
  DAYS.forEach((jour) => {
    jours[jour] = null;
  });

  let alwaysOpen = false;

  openingHours.forEach((entree) => {
    if (!entree || typeof entree !== 'object') return;

    const cle = DAY_MAP[String(entree.day ?? '').trim().toLowerCase()];
    if (!cle) return; // jour inconnu → entrée ignorée, jamais devinée

    if (estAlwaysOpen(entree.hours)) {
      alwaysOpen = true;
      return;
    }

    jours[cle] = parsePlages(entree.hours);
  });

  // Objet reconstruit dans l'ordre DAYS : l'ordre du fichier source est ignoré (piège n°2).
  // `alwaysOpen` reste la dernière clé, comme dans le schéma §2.
  return { ...jours, alwaysOpen };
};

/** `"18:00"` → 1080. `null` si la valeur n'est pas une heure. */
const enMinutes = (heure) => {
  const [h, m] = String(heure).split(':');
  const heures = Number(h);
  const minutes = Number(m);
  if (Number.isNaN(heures) || Number.isNaN(minutes)) return null;
  return heures * 60 + minutes;
};

/** `1080` → `"18:00"`. */
const enHeure = (minutes) =>
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;

/**
 * Premier jour suivant `jourActuel` qui a au moins une plage.
 * @returns {{jour: string, jourLabel: string, ouverture: string}|null}
 */
export const prochainJourOuvert = (openingHours, jourActuel) => {
  if (!openingHours) return null;

  const index = DAYS.indexOf(jourActuel);
  if (index === -1) return null;

  for (let decalage = 1; decalage <= 7; decalage += 1) {
    const jour = DAYS[(index + decalage) % 7];
    const plages = Array.isArray(openingHours[jour]) ? openingHours[jour] : null;
    if (plages && plages.length > 0) {
      return { jour, jourLabel: DAYS_LABELS[jour], ouverture: plages[0].split('-')[0] };
    }
  }

  return null;
};

/**
 * État d'ouverture **calculé à l'instant du rendu** — remplace `wasOpenAtScrapeTime` (C7),
 * qui reflétait l'heure du scrape (20h31 → 9 fiches sur 50) et non celle de l'appel.
 *
 * Les horaires Google sont en heure locale de l'établissement (Europe/Paris) et le poste de
 * l'admin est en France : aucune conversion de fuseau n'est faite. Limitation assumée.
 *
 * @param {object|null} openingHours objet normalisé par `parseOpeningHours`
 * @param {Date} date instant de référence (injectable pour les tests)
 * @returns {{ouvert: boolean|null, plagesDuJour: string[]|null, fermeA: string|null,
 *            ouvreA: string|null, jourActuel: string, prochainJour: object|null}}
 */
export const estOuvertMaintenant = (openingHours, date = new Date()) => {
  // JS : 0 = dimanche → le décalage de 6 est obligatoire (test 9)
  const jourActuel = DAYS[(date.getDay() + 6) % 7];
  const maintenant = date.getHours() * 60 + date.getMinutes();

  if (!openingHours) {
    // 12 fiches : horaires inconnus → `null`, surtout pas `false` (sinon l'agent saute une fiche
    // appelable en croyant qu'elle est fermée).
    return {
      ouvert: null,
      plagesDuJour: null,
      fermeA: null,
      ouvreA: null,
      jourActuel,
      prochainJour: null
    };
  }

  if (openingHours.alwaysOpen === true) {
    return {
      ouvert: true,
      plagesDuJour: null,
      fermeA: null,
      ouvreA: null,
      jourActuel,
      prochainJour: null
    };
  }

  const plagesDuJour = Array.isArray(openingHours[jourActuel]) ? openingHours[jourActuel] : null;

  if (!plagesDuJour || plagesDuJour.length === 0) {
    return {
      ouvert: false,
      plagesDuJour: null,
      fermeA: null,
      ouvreA: null,
      jourActuel,
      prochainJour: prochainJourOuvert(openingHours, jourActuel)
    };
  }

  let fermeA = null;
  let ouvreA = null;

  plagesDuJour.forEach((plage) => {
    const [debut, fin] = plage.split('-');
    const debutMin = enMinutes(debut);
    const finMin = enMinutes(fin);
    if (debutMin === null || finMin === null) return;

    if (maintenant >= debutMin && maintenant < finMin) {
      fermeA = fin;
    } else if (maintenant < debutMin && ouvreA === null) {
      ouvreA = debut;
    }
  });

  return {
    ouvert: fermeA !== null,
    plagesDuJour,
    fermeA,
    ouvreA,
    jourActuel,
    prochainJour: fermeA !== null ? null : prochainJourOuvert(openingHours, jourActuel)
  };
};

/**
 * Badge lisible pour la file d'appel :
 * `🟢 Ouvert · ferme à 18:00` / `🔴 Fermé · ouvre lundi 08:30` / `⚪ Horaires inconnus`
 */
export const formaterEtatOuverture = (etat) => {
  if (!etat || etat.ouvert === null || etat.ouvert === undefined) {
    return { icone: '⚪', texte: 'Horaires inconnus', ton: 'neutre' };
  }

  if (etat.ouvert) {
    return {
      icone: '🟢',
      texte: etat.fermeA ? `Ouvert · ferme à ${etat.fermeA}` : 'Ouvert',
      ton: 'succes'
    };
  }

  if (etat.ouvreA) {
    return { icone: '🔴', texte: `Fermé · ouvre à ${etat.ouvreA}`, ton: 'echec' };
  }

  if (etat.prochainJour) {
    return {
      icone: '🔴',
      texte: `Fermé · ouvre ${etat.prochainJour.jourLabel} ${etat.prochainJour.ouverture}`,
      ton: 'echec'
    };
  }

  return { icone: '🔴', texte: 'Fermé', ton: 'echec' };
};

/** Résumé texte des plages d'une journée, pour l'affichage de la fiche. */
export const formaterPlages = (plages) => {
  if (!Array.isArray(plages) || plages.length === 0) return 'Fermé';
  return plages.join(' · ');
};

export { enHeure };
